import { getSupabase } from '@/lib/supabase';
import type { TeamPreset } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { decodeRowToTeamPreset, type TeamPresetRow } from '../codec';
import { getUserSyncState, setUserLastDownloadedAt } from '../state';

export interface DownloadResult {
  presets: TeamPreset[];
}

export async function downloadRemoteTeamPresets(userId: UserId): Promise<DownloadResult> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync team presets');
  }

  const { lastDownloadedAt = 0 } = await getUserSyncState(userId, 'team_presets');
  // Download all team presets that were created/updated/deleted after lastDownloadedAt
  const { data, error } = await client
    .from('team_presets')
    .select<'*', TeamPresetRow>('*')
    .eq('user_id', userId)
    .or(
      `created_at.gt.${lastDownloadedAt},updated_at.gt.${lastDownloadedAt},deleted_at.gt.${lastDownloadedAt}`,
    )
    .order('updated_at', { ascending: true })
    .limit(1000);
  if (error) throw error;
  const presets = (data ?? []).map((row) => decodeRowToTeamPreset(row));
  if (presets.length > 0) {
    const maxTs = Math.max(
      ...presets.map((p) => Math.max(p.createdAt, p.updatedAt, p.deletedAt ?? 0)),
      0,
    );
    await setUserLastDownloadedAt(userId, 'team_presets', maxTs);
  }
  return { presets };
}
